import {
  CourseMaterial,
  useCoursesLoaderData,
  useProgramDetails,
} from "./coursesLoader.ts";
import styles from "./MaterialAccessBlock.module.scss";
import { FormattedMessage } from "react-intl";
import Markdown from "react-markdown";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import OffScreen from "../../../../common/core-ui/OffScreen.tsx";
import { clsx } from "clsx";
import useOptedIn from "./useOptedIn.tsx";
import useMaterialOptDeadline from "./useMaterialOptDeadline.tsx";
import { useLayoutData } from "../layout/myCoursesLayoutLoader.ts";
import useFindCourse from "./useFindCourse.tsx";
import { Action } from "./useMaterialActions.tsx";
import PillTag from "../common/PillTag.tsx";
import { useMiniPdpContext } from "../../common/product/mini-pdp/miniPdpContext.ts";
import useInclusiveAccessMaterialPrice from "./MaterialPanel/useInclusiveAccessMaterialPrice.tsx";

function IAOptedInBlock({
  material,
  optOutAction,
}: {
  material: CourseMaterial;
  optOutAction?: Action;
}) {
  const { name: programName } = useProgramDetails();
  const materialPrice = useInclusiveAccessMaterialPrice(material);
  return (
    <div className={styles.container} data-testid="ia-block-in">
      <div className={styles.info}>
        <PillTag type="OptedIn" />
        <div className={styles.message}>{programName}</div>
        {materialPrice && (
          <div className={styles.pricing}>
            <FormattedMessage
              defaultMessage={
                "<strong>{itemPrice}</strong>, billed to student account."
              }
              values={{
                itemPrice: materialPrice,
                strong: (content) => <strong>{content}</strong>,
              }}
            />
          </div>
        )}
      </div>
      {optOutAction && (
        <div className={styles.action}>
          <BaseLinkOrButton
            aria-label={optOutAction.ariaLabel}
            className="vst-link vst-link-size-small"
            onClick={() => void optOutAction.onClick?.()}
          >
            {optOutAction.label}
          </BaseLinkOrButton>
        </div>
      )}
    </div>
  );
}

function IAOptedOutBlock({
  material,
  optInAction,
  allowOptInAfterDeadline,
}: {
  material: CourseMaterial;
  optInAction?: Action;
  allowOptInAfterDeadline?: boolean;
}) {
  const {
    scope,
    userEffectiveProgram,
    allowsPrint,
    ia: { name: programName },
    ea: { name: eaProgramName },
  } = useProgramDetails();
  const { optDeadline, optWindowOpen } = useMaterialOptDeadline(material);
  const materialPrice = useInclusiveAccessMaterialPrice(material);

  /*
  TODO This is temporarily disabled but it is coming back so leave it in for now
  // I checked, saving is based off highest store price to inflate the saving...
  const highestStorePrice = (
    material.product?.map((v) => v.variants ?? []).flat() ?? []
  ).reduce<number>((acc, cur) => {
    const curPrice = Number(cur.studentPrice);
    return isNaN(curPrice) || curPrice < acc ? acc : curPrice;
  }, 0);
  let saving = (highestStorePrice ?? 0) - (material?.studentPrice ?? 0);
  if (saving < 0) {
    // don't think should happen for real, but call it out with a zero
    // save -$12.00 (i.e. you are getting ripped off) might be interpreted as a discount
    // the other option is "pay $12.00 more by opting in" which seems ridiculous
    saving = 0;
  }

  const savingAmount = intl.formatNumber(saving, {
    style: "currency",
    currency: material.currency ?? "USD",
  });
  Original message:
  Save {savingAmount} by opting back into {programName}

   */

  return (
    <div className={clsx(styles.container)} data-testid="ia-block-out">
      <div className={styles.info}>
        <PillTag type="OptedOut" />
        <div className={styles.pricing}>
          {scope === "flexible" &&
          material.materialFormat === "print" &&
          !allowsPrint ? (
            <FormattedMessage
              tagName="div"
              defaultMessage="Opt back in to {eaProgramName} or purchase on your own."
              values={{
                eaProgramName,
              }}
            />
          ) : (
            <>
              {materialPrice && (
                <FormattedMessage
                  tagName="div"
                  defaultMessage={
                    "Program Price: <strong>{itemPrice}</strong>."
                  }
                  values={{
                    itemPrice: materialPrice,
                    strong: (content) => <strong>{content}</strong>,
                  }}
                />
              )}
              <div>
                <FormattedMessage
                  defaultMessage={"Save by opting back into {programName}."}
                  values={{
                    programName,
                  }}
                />
              </div>
            </>
          )}
          {optDeadline && optWindowOpen && !allowOptInAfterDeadline && (
            <FormattedMessage
              tagName="div"
              defaultMessage="Opt in until <strong>{date, date, long}</strong>."
              values={{
                strong: (content) => <strong>{content}</strong>,
                date: new Date(optDeadline),
              }}
            />
          )}
          {userEffectiveProgram === "inclusive_access" &&
            material.materialFormat === "print" &&
            !allowsPrint && (
              <FormattedMessage
                tagName="div"
                defaultMessage="<em>Physical materials are not available in your school's {programName} Program.</em>"
                values={{
                  programName,
                  em: (content) => <em>{content}</em>,
                }}
              />
            )}
        </div>
        {allowOptInAfterDeadline && !optWindowOpen && optInAction && (
          <div className={styles.lateOptWarning}>
            <FormattedMessage defaultMessage="You will not be able to opt back out." />
          </div>
        )}
      </div>
      {optInAction && (
        <div className={styles.action}>
          <BaseLinkOrButton
            aria-label={optInAction.ariaLabel}
            className="vst-button-pill"
            onClick={() => void optInAction.onClick?.()}
          >
            {optInAction.label}
          </BaseLinkOrButton>
        </div>
      )}
    </div>
  );
}

function EAOptedInBlock() {
  const {
    name: programName,
    ea: { itemCardDescription },
  } = useProgramDetails();
  return (
    <div className={styles.container} data-testid="ea-block-in">
      <div className={styles.info}>
        <PillTag type="OptedIn" />
        <div className={styles.message}>{programName}</div>
        <div className={styles.pricing}>{itemCardDescription}</div>
      </div>
    </div>
  );
}

function EAOptedOutBlock({ material }: { material: CourseMaterial }) {
  const {
    ea: { name: eaProgramName },
  } = useProgramDetails();
  const { optDeadline } = useMaterialOptDeadline(material);
  const { isOutOfStock, isUnavailable } = useMiniPdpContext();

  return (
    <div className={clsx(styles.container)} data-testid="ea-block-out">
      <div className={styles.info}>
        <PillTag type="OptedOut" />
        <div className={styles.message}>
          {isOutOfStock || isUnavailable ? (
            <FormattedMessage
              defaultMessage="Opt back in to {eaProgramName}."
              values={{
                eaProgramName,
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="Opt back in to {eaProgramName} or purchase on your own."
              values={{
                eaProgramName,
              }}
            />
          )}
        </div>
        {optDeadline && (
          <FormattedMessage
            tagName="div"
            defaultMessage="Opt back in until {date, date, long}."
            values={{
              date: new Date(optDeadline),
            }}
          />
        )}
      </div>
    </div>
  );
}

function IncludedBlock() {
  return (
    <div
      className={clsx(styles.container, styles.included)}
      data-testid="included-block"
    >
      <FormattedMessage defaultMessage="This is a no-cost course material that’s included as part of your course." />
    </div>
  );
}

function PendingLicenseEventBlock() {
  const { name } = useProgramDetails();
  const { config } = useLayoutData();

  if (!config.lateAddWarning) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="pending-license-block">
      <div className={styles.info}>
        <div className={styles.message}>{name}</div>
        <div className={styles.pricing}>
          <Markdown
            allowedElements={["a"]}
            unwrapDisallowed={true}
            components={{
              a: ({ children, node, ...rest }) => (
                <a
                  className="vst-link vst-link-size-small"
                  target="_blank"
                  rel="noopener noreferrer"
                  {...rest}
                >
                  {children}
                  <OffScreen tagName="span">
                    <FormattedMessage defaultMessage="(opens in a new window)" />
                  </OffScreen>
                </a>
              ),
            }}
          >
            {config.lateAddWarning}
          </Markdown>
        </div>
      </div>
    </div>
  );
}

export default function MaterialAccessBlock({
  material,
  optOutAction,
  optInAction,
}: {
  material: CourseMaterial;
  optOutAction?: Action;
  optInAction?: Action;
}) {
  const {
    scope,
    userEffectiveProgram,
    ea: { allowsOptOut },
  } = useProgramDetails();
  const optedIn = useOptedIn(material);
  const { optWindowOpen } = useMaterialOptDeadline(material);
  const isInstructor =
    useFindCourse({ courseId: material.courseId })?.userRole === "instructor";
  const { currentTerm } = useCoursesLoaderData();
  const allowOptInAfterDeadline = currentTerm?.allowOptInAfterDeadline ?? false;

  if (material.verbaLicense?.pending) {
    return <PendingLicenseEventBlock />;
  }

  if (
    !material.materialStatus ||
    material.materialStatus === "not_included" ||
    material.businessModels === "Locked Participation" ||
    (isInstructor && material.businessModels === "ePOS")
  ) {
    return null;
  }

  if (material.materialStatus === "included") {
    return <IncludedBlock />;
  }

  if (!optWindowOpen && !allowOptInAfterDeadline) {
    return null;
  }

  const eaLogic = () => {
    if (!allowsOptOut) {
      return null;
    }
    return optedIn ? (
      <EAOptedInBlock />
    ) : (
      <EAOptedOutBlock material={material} />
    );
  };

  const iaLogic = () => {
    return optedIn ? (
      <IAOptedInBlock material={material} optOutAction={optOutAction} />
    ) : (
      <IAOptedOutBlock
        material={material}
        optInAction={optInAction}
        allowOptInAfterDeadline={allowOptInAfterDeadline}
      />
    );
  };

  if (scope === "flexible") {
    if (
      userEffectiveProgram === "no_program" ||
      userEffectiveProgram === "inclusive_access"
    ) {
      return iaLogic();
    } else if (userEffectiveProgram === "equitable_access") {
      return eaLogic();
    }
  } else if (scope === "ea_only") {
    return eaLogic();
  } else if (scope === "ia_only") {
    return iaLogic();
  }

  return null;
}
